import { Popover as CompassPopover } from "@circleco/compass/components/Popover";
import type { Editor } from "@tiptap/react";
import { noop } from "lodash";
import { useState } from "react";

import { buildVirtualAnchor } from "./buildVirtualAnchor";
import { PickerContent } from "./PickerContent";
import type {
  AddToLocalInlineAttachments,
  EditorRange,
  ShowGiphyPickerArgs,
  VirtualAnchor,
} from "./types";

interface PopoverState {
  addToLocalInlineAttachments: AddToLocalInlineAttachments;
  anchor: VirtualAnchor;
  editor: Editor;
  range: EditorRange;
}

export interface UseGiphyPickerPopoverReturn {
  element: React.ReactNode;
  remove: () => void;
  show: (args: ShowGiphyPickerArgs) => void;
}

export const useGiphyPickerPopover = (): UseGiphyPickerPopoverReturn => {
  const [state, setState] = useState<PopoverState | null>(null);

  const show = ({
    editor,
    range,
    addToLocalInlineAttachments = noop,
  }: ShowGiphyPickerArgs) => {
    const fromPos = range?.from || editor.state.selection.from;
    const resolvedRange: EditorRange =
      range?.from || range?.to
        ? { from: range.from, to: range.to }
        : { from: fromPos, to: fromPos };
    const anchor = buildVirtualAnchor(editor, fromPos);
    if (!anchor) return;
    setState({
      editor,
      range: resolvedRange,
      addToLocalInlineAttachments,
      anchor,
    });
  };

  const remove = () => setState(null);

  const element = state ? (
    <CompassPopover
      anchor={state.anchor}
      open
      onOpenChange={isOpen => {
        if (!isOpen) remove();
      }}
      side="top"
      align="start"
      sideOffset={8}
    >
      <PickerContent
        editor={state.editor}
        range={state.range}
        addToLocalInlineAttachments={state.addToLocalInlineAttachments}
        onClose={remove}
      />
    </CompassPopover>
  ) : null;

  return { show, remove, element };
};
